import * as React from "react";
import { cn } from "../../lib/utils";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface SheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  label: string;
  children: React.ReactNode;
  finalFocusRef?: React.RefObject<HTMLElement>;
  className?: string;
}

/** Accessible modal drawer with focus entry, containment, and restoration. */
export function Sheet({
  open,
  onOpenChange,
  label,
  children,
  finalFocusRef,
  className,
}: SheetProps) {
  const contentRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const content = contentRef.current;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const frame = requestAnimationFrame(() => {
      (content?.querySelector<HTMLElement>(FOCUSABLE) ?? content)?.focus();
    });
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onOpenChange(false);
        return;
      }
      if (event.key !== "Tab" || !content) return;
      const focusable = Array.from(content.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) {
        event.preventDefault();
        content.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = oldOverflow;
      requestAnimationFrame(() => (finalFocusRef?.current ?? previouslyFocused)?.focus());
    };
  }, [finalFocusRef, onOpenChange, open]);

  if (!open) return null;
  return (
    <div
      className="lg:hidden fixed inset-0 z-40 bg-black/50 animate-fade-in"
      onMouseDown={(event) => event.target === event.currentTarget && onOpenChange(false)}
    >
      <div
        ref={contentRef}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
        className={cn(
          "absolute inset-y-0 left-0 animate-fade-in-up focus:outline-none",
          className,
        )}
      >
        {children}
      </div>
    </div>
  );
}
